import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { getAllowedScoringFormats, getDefaultAllottedTimeForScoringType, getQualifyingDrawEliminationTypes } from '../../../utils/events';
import { drawSizes } from '../../../utils/constants';
import { sportTypeIds } from '../../../utils/eventConstants';
import { useStatsig } from '../../hooks/useStatsig';

const useQualifyingDraw = (params) => {
    const {
        sportTypeId,
        eventTypeId,
        field,
        formApi,
        forceDoubles = false,
        eventAllottedMatchTimes,
        timeZone,
    } = params;

    const coreUI = useSelector(({ coreUI }) => coreUI);

    const {
        drawEliminationTypes,
        scoringTypes,
    } = coreUI.types.tms.division.tournament;

    const { checkGate, featureGates } = useStatsig();
    const isFeedInConsolationFlagEnabled = checkGate(featureGates.FEED_IN_CONSOLATION_DRAWS);
    const isAllottedTimeFlagEnabled = checkGate(featureGates.ALLOTTED_MATCH_TIMES);

    const [selectableDrawSizes, setSelectableDrawSizes] = useState(forceDoubles ? drawSizes('teams') : drawSizes('players'));
    const [dateToDisplay, setDateToDisplay] = useState(null);

    const qualifyingDrawEliminationTypes = getQualifyingDrawEliminationTypes(
        drawEliminationTypes,
        isFeedInConsolationFlagEnabled,
        true,
    );
    const selectableScoringTypes = scoringTypes.filter(type => {
        return type.sportTypeId === sportTypeId;
    });

    const getScoringFormats = (values) => {
        if (sportTypeId === sportTypeIds.PICKLEBALL) {
            return selectableScoringTypes;
        }

        return getAllowedScoringFormats(selectableScoringTypes, eventTypeId, values.formatTypeId, values.ballTypeId);
    };

    const onQualifyingDrawEliminationChange = () => {
        formApi.reset([field, 'drawSize']);
        formApi.reset([field, 'feedOutRound']);
    };

    const onDrawSizeChange = () => {
        formApi.reset([field, 'feedOutRound']);
    };

    const onChangeDrawEnabled = (checked) => {
        if (!checked) {
            formApi.reset(field);

            if (field === 'qualifyingDraw') {
                formApi.reset('preQualifyingDraw');
            }
        }

        if (checked) {
            if (field === 'qualifyingDraw') {
                formApi.setValue([field, 'name'], 'Qualifying draw');
            } else {
                formApi.setValue([field, 'name'], 'Pre-qualifying draw');
            }
        }
    };

    const onChangeScoringType = (scoringTypeId) => {
        const allottedTime = getDefaultAllottedTimeForScoringType(scoringTypeId, eventAllottedMatchTimes, scoringTypes);
        formApi.setValue([field, 'allottedMatchTime'], allottedTime);
    };

    const handleDateChange = (fieldName, formApi) => (dateTime) => {
        setDateToDisplay(dateTime);
        formApi.setValue([field, fieldName], dateTime?.toISOString() || null);
    };

    useEffect(() => {
        setSelectableDrawSizes(forceDoubles ? drawSizes('teams') : drawSizes('players'));
    }, [forceDoubles]);

    useEffect(() => {
        const startDateTimeUtc = formApi.getValue([field, 'startDateTimeUtc']);
        if (startDateTimeUtc) {
            const dateTime = moment.utc(startDateTimeUtc).tz(timeZone?.value);
            setDateToDisplay(dateTime);
        }
    }, [timeZone]);

    return {
        isAllottedTimeFlagEnabled,
        selectableDrawSizes,
        qualifyingDrawEliminationTypes,
        dateToDisplay,
        onQualifyingDrawEliminationChange,
        onDrawSizeChange,
        onChangeDrawEnabled,
        getScoringFormats,
        onChangeScoringType,
        handleDateChange,
    };
};

export default useQualifyingDraw;
